'use client';

import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import Link from 'next/link';
import Image from 'next/image';

interface MakerProfileClientProps {
  maker: any;
}

export default function MakerProfileClient({ maker }: MakerProfileClientProps) {
  const pageRef = useScrollAnimation({ threshold: 0.1 });

  // Social platform icons
  const getSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
          </svg>
        );
      case 'twitter':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
          </svg>
        );
      case 'website':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="2" y1="12" x2="22" y2="12"/>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
        );
      case 'linkedin':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
            <circle cx="4" cy="4" r="2"/>
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      {/* Hero Section with Background */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] h-[300px] sm:h-[400px] bg-gray-900 overflow-hidden">
          <Image
            src={maker.backgroundImage?.sourceUrl || "https://images.unsplash.com/photo-1604076913837-52ab5629fba9?w=1440&h=400&fit=crop&auto=format"}
            alt={maker.backgroundImage?.altText || `${maker.title} background`}
            fill
            priority
            className="object-cover"
            sizes="(max-width: 1440px) 100vw, 1440px"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/80" />
          <div className="absolute inset-0 flex items-end">
            <div className="w-full px-6 sm:px-8 lg:px-12 pb-8">
              <div className="flex items-end gap-6">
                {/* Profile Image */}
                <div className="relative w-24 h-24 sm:w-32 sm:h-32 overflow-hidden border-4 border-white shadow-xl">
                  <Image
                    src={maker.profileImage?.sourceUrl || "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=300&h=300&fit=crop&auto=format"} 
                    alt={maker.profileImage?.altText || maker.title}
                    fill
                    priority
                    className="object-cover"
                    sizes="(max-width: 640px) 96px, 128px"
                  />
                </div>
                
                {/* Basic Info */}
                <div className="flex-1 mb-2">
                  <h1 className="text-[28px] sm:text-[36px] lg:text-[42px] text-white mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
                    {maker.title}
                  </h1>
                  <p className="font-ibm-plex-sans text-[16px] text-white/80">
                    {maker.makerLocation}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full flex justify-center">
        <div ref={pageRef} className="animate-on-scroll relative w-full max-w-[1440px] bg-white px-6 sm:px-8 lg:px-12 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Bio & Skills */}
            <div className="lg:col-span-2">
              {/* Bio */}
              <div className="mb-8">
                <h2 className="font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase text-black/50 mb-4">
                  About
                </h2>
                <p className="font-ibm-plex-sans text-[16px] text-black/80 leading-relaxed">
                  {maker.makerBio || maker.excerpt}
                </p>
              </div>

              {/* Specialties */}
              {maker.makerSpecialties?.nodes && maker.makerSpecialties.nodes.length > 0 && (
                <div className="mb-8">
                  <h2 className="font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase text-black/50 mb-4">
                    Specialties
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {maker.makerSpecialties.nodes.map((specialty: any) => (
                      <span 
                        key={specialty.slug}
                        className="bg-gray-100 border border-gray-300 text-gray-700 px-3 py-1 text-[10px] font-ibm-plex-sans-condensed tracking-[1px] uppercase"
                      >
                        {specialty.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Extended Bio from Content */}
              {maker.content && (
                <div className="mb-8">
                  <h2 className="font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase text-black/50 mb-4">
                    Extended Bio
                  </h2>
                  <div 
                    className="prose prose-lg max-w-none font-ibm-plex-sans"
                    dangerouslySetInnerHTML={{ __html: maker.content }}
                  />
                </div>
              )}
            </div>

            {/* Right Column - Contact & Social */}
            <div>
              {/* Social Links */}
              {maker.socialLinks && maker.socialLinks.length > 0 && (
                <div className="mb-8">
                  <h2 className="font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase text-black/50 mb-4">
                    Connect
                  </h2>
                  <div className="flex flex-col gap-3">
                    {maker.socialLinks.map((social: any, index: any) => (
                      <a
                        key={index}
                        href={social.socialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="text-[#e7131a]">
                          {getSocialIcon(social.socialPlatform)}
                        </div>
                        <span className="font-ibm-plex-sans text-[14px] text-black">
                          {social.socialPlatform}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Card */}
              <div className="bg-black p-6">
                <h3 className="text-[20px] text-white mb-3" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
                  Interested in Collaboration?
                </h3>
                <p className="font-ibm-plex-sans text-[14px] text-white/70 mb-4">
                  Connect with {maker.title} for AI projects and creative collaborations.
                </p>
                <button className="w-full bg-[#e7131a] text-white py-3 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase transition-all duration-300 hover:bg-[#c10e14]">
                  Get in Touch
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Section - Placeholder for future */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-white border-t border-black/10 px-6 sm:px-8 lg:px-12 py-12">
          <h2 className="text-[28px] sm:text-[32px] text-black mb-8" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
            Projects by {maker.title}
          </h2>
          <div className="text-center py-12 bg-gray-50">
            <p className="font-ibm-plex-sans text-[16px] text-black/50">
              Projects will be displayed here once available.
            </p>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-black py-16 px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="text-[28px] sm:text-[32px] lg:text-[36px] text-white mb-4" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
              Discover More Makers
            </h2>
            <p className="font-ibm-plex-sans text-[16px] text-white/70 mb-8 max-w-2xl mx-auto">
              Join our community to connect with innovative AI creators and stay updated on the latest projects.
            </p>
            <div className="flex gap-4 justify-center">
              <Link href="/makers">
                <button className="bg-white text-black px-8 py-3 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase transition-all duration-300 hover:bg-gray-100">
                  Browse All Makers
                </button>
              </Link>
              <button className="bg-[#e7131a] text-white px-8 py-3 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase transition-all duration-300 hover:bg-[#c10e14]">
                Join Community
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}